import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { QueryConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import { api, extractData } from '@/lib/treaty';

type MeQueryOptions = {
  queryConfig?: QueryConfig<typeof getMeQueryOptions>;
  tenantId?: string | undefined;
};

export type MeUser = Awaited<ReturnType<typeof getMe>>;

type IpLocation = {
  ip: string;
  latitude: number;
  longitude: number;
};

export const getMe = (tenantId?: string | undefined) => {
  return extractData(
    api.users.me.get({
      query: {
        tenantId: tenantId,
      },
    })
  );
};

export const getMeQueryOptions = (tenantId?: string | undefined) => {
  return queryOptions({
    queryKey: ['users', 'me', tenantId],
    queryFn: () => getMe(tenantId),
  });
};

export const ensureMeQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getMeQueryOptions(params.tenantId));
};

export const useMe = ({ queryConfig, tenantId }: MeQueryOptions = {}) => {
  return useSuspenseQuery({ ...getMeQueryOptions(tenantId), ...queryConfig });
};

// Default location - Austria
const DEFAULT_LOCATION: IpLocation = {
  ip: '',
  latitude: 48.2082,
  longitude: 16.3738,
};

const getIpLocation = async (): Promise<IpLocation> => {
  try {
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) {
      throw new Error('Failed to fetch location');
    }
    const data = await res.json<IpLocation>();
    return {
      ip: data.ip,
      latitude: data.latitude,
      longitude: data.longitude,
    };
  } catch {
    return DEFAULT_LOCATION;
  }
};

export const useMyIpLocation = () => {
  return useQuery<IpLocation>({
    queryKey: ['IP_LOCATION'],
    queryFn: getIpLocation,
    staleTime: 10 * 60_000,
  });
};
